import type { UseQueryOptions } from "@tanstack/react-query";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./apiClient";
import type {
  CreateUserPayload,
  CreatedUser,
  TenantHierarchy,
  UserRolesHierarchy,
  UsersByOrganisationId,
  UsersRoleAssignmentsOnTenant,
  ResponsibleUsers,
  UpdateRoleResponse,
  UpdateRolePayload,
} from "./users.types";
import { userInfoKeys } from "./userInfo";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import { isSameQueryKey } from "./utils";
import { useCurrentOrganisation } from "../root/CurrentOrganisationProvider";
import { prefetch } from "./prefetch";
import { hasErrorCode } from "./errors";

const FORMS_ORG_ENDPOINT = `${FORMS_API_BASE_ENDPOINT}/org/users`;
const FORMS_ADMIN_ENDPOINT = `${FORMS_API_BASE_ENDPOINT}/admin/users`;
const FORMS_API_ENDPOINT = `${FORMS_API_BASE_ENDPOINT}/users`;
const usersKeys = {
  all: () => ["users"] as const,
  users: () => [...usersKeys.all(), "users"] as const,
  userGridView: () => [...usersKeys.all(), "gridView"] as const,
  userRolesHierarchy: (userId: string | undefined) => [...usersKeys.all(), "UserRolesHierarchy", { userId }] as const,
  userRoleAssignmentsOnTenant: (tenantId: string | undefined) =>
    [...usersKeys.all(), "UsersRoleAssignments", { tenantId }] as const,
  responsibleUsersForTenant: (tenantId: string | undefined) =>
    [...usersKeys.all(), "ResponsibleUsers", { tenantId }] as const,
  responsibleUsersForTenantDpia: (tenantId: string | undefined) =>
    [...usersKeys.all(), "ResponsibleUsersDpia", { tenantId }] as const,
  replaceResponsibleCandidatesAll: () => [...usersKeys.all(), "ReplaceResponsibleCandidates"] as const,
  replaceResponsibleCandidates: (userId: string, selection: ReplaceResponsibleSelection) =>
    [...usersKeys.replaceResponsibleCandidatesAll(), { userId, selection }] as const,
  myTenantsHierarchy: () => [...usersKeys.all(), "my"] as const,
};

/** Get users from current organisation */
const usersFromCurrentOrganisationQueryOptions = () =>
  queryOptions({
    queryKey: usersKeys.users(),
    queryFn: () => api.get(`${FORMS_ORG_ENDPOINT}`) as Promise<UsersByOrganisationId>,
    throwOnError: true,
  });

export const prefetchUsersFromCurrentOrganisation = () => prefetch(usersFromCurrentOrganisationQueryOptions());

export const useGetUsersFromCurrentOrganisation = (options: Pick<UseQueryOptions, "enabled"> = { enabled: true }) => {
  return useQuery({
    ...usersFromCurrentOrganisationQueryOptions(),
    enabled: Boolean(options.enabled),
  });
};

/** Create user */
export const useCreateUser = () => {
  const queryClient = useQueryClient();
  const createUser = (payload: CreateUserPayload) => api.post(FORMS_ORG_ENDPOINT, payload) as Promise<CreatedUser>;

  return useMutation({
    mutationFn: createUser,
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

/** Delete user */
export const useDeleteUser = () => {
  const currentOrganisation = useCurrentOrganisation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (idToDelete: string) =>
      api.delete(`${FORMS_ADMIN_ENDPOINT}/${idToDelete}/organizations/${currentOrganisation.id}`),
    onSuccess: (_, idToDelete) => {
      queryClient.invalidateQueries({
        predicate: query => !isSameQueryKey(query.queryKey, usersKeys.userRolesHierarchy(idToDelete)),
      });
    },
  });
};

/** Get users role assignments by tenant id */
export const useGetUsersRoleAssignments = (tenantId: string | undefined) => {
  const getUsersRoleAssignments = () => {
    if (!tenantId) throw new Error('"tenantId" should not be null');
    return api.get(`${FORMS_ORG_ENDPOINT}/roles/tenant/${tenantId}`) as Promise<UsersRoleAssignmentsOnTenant>;
  };

  return useQuery({
    queryKey: usersKeys.userRoleAssignmentsOnTenant(tenantId),
    queryFn: getUsersRoleAssignments,
    enabled: !!tenantId,
    throwOnError: true,
  });
};

/** Get user info roles hierarchy by user  */
export const useGetUserRolesHierarchy = (userId: string | undefined) => {
  const getUserRolesHierarchy = () => {
    if (!userId) throw new Error('"userId" should not be null');
    return api.get(`${FORMS_ORG_ENDPOINT}/${userId}/rolesHierarchy`) as Promise<UserRolesHierarchy>;
  };

  return useQuery({
    queryKey: usersKeys.userRolesHierarchy(userId),
    queryFn: getUserRolesHierarchy,
    enabled: !!userId,
    throwOnError: true,
  });
};

/** Get responsible users by tenant id */
export const useGetResponsibleUsers = (
  tenantId: string,
  options: Pick<UseQueryOptions, "enabled"> = { enabled: true }
) => {
  return useQuery({
    queryKey: usersKeys.responsibleUsersForTenant(tenantId),
    queryFn: () => api.get(`${FORMS_ORG_ENDPOINT}/responsibles/tenant/${tenantId}`) as Promise<ResponsibleUsers>,
    enabled: Boolean(options.enabled),
    throwOnError: true,
  });
};

/** Get my tenant hierarchy */
export const useGetMyTenantsHierarchy = () => {
  const getMyTenantsHierarchy = () => api.get(`${FORMS_ORG_ENDPOINT}/me/tenantHierarchy`) as Promise<TenantHierarchy>;

  return useQuery({
    queryKey: usersKeys.myTenantsHierarchy(),
    queryFn: getMyTenantsHierarchy,
    throwOnError: true,
  });
};

/** Update roles Hierarchy  */
export const useUpdateRoleHierarchy = () => {
  const updateRolesHierarchyCache = useUpdateRolesHierarchyCache();

  const updateRoleHierarchy = ({ userId, payload }: { userId: string; payload: UpdateRolePayload }) =>
    api.patch(`${FORMS_ADMIN_ENDPOINT}/${userId}/rolesHierarchy`, payload) as Promise<
      UpdateRoleResponse<UserRolesHierarchy>
    >;

  return useMutation({
    mutationFn: updateRoleHierarchy,
    onSuccess: updateRolesHierarchyCache,
  });
};

/** Update roles hierarchy cache per user */
const useUpdateRolesHierarchyCache = () => {
  const queryClient = useQueryClient();

  return (response: UpdateRoleResponse<UserRolesHierarchy>) => {
    if (!response.fulfilled) return;
    /** Update roles hierarchy by user id */
    queryClient.invalidateQueries({ queryKey: userInfoKeys.user() });
    const updatedRolesHierarchy = response.result;
    queryClient.setQueryData(usersKeys.userRolesHierarchy(updatedRolesHierarchy.user.userId), updatedRolesHierarchy);
  };
};

/** Update user role on tenant */
export const useUpdateRoleOnTenant = () => {
  const updateRoleAssignmentsCache = useUpdateRoleAssignmentCache();

  const updateRoleOnTenant = ({
    userId,
    tenantId,
    payload,
  }: {
    userId: string;
    tenantId: string;
    payload: UpdateRolePayload;
  }) =>
    api.patch(`${FORMS_ADMIN_ENDPOINT}/${userId}/roles/tenant/${tenantId}`, payload) as Promise<
      UpdateRoleResponse<UsersRoleAssignmentsOnTenant>
    >;

  return useMutation({
    mutationFn: updateRoleOnTenant,
    onSuccess: updateRoleAssignmentsCache,
  });
};

/** Update role assignment cache */
const useUpdateRoleAssignmentCache = () => {
  const queryClient = useQueryClient();

  return (response: UpdateRoleResponse<UsersRoleAssignmentsOnTenant>) => {
    if (!response.fulfilled) return;

    /** Update users role assignments on tenant */
    queryClient.invalidateQueries({ queryKey: userInfoKeys.user() });
    const updatedRoleAssignments = response.result;
    queryClient.setQueryData(
      usersKeys.userRoleAssignmentsOnTenant(updatedRoleAssignments.tenantId),
      updatedRoleAssignments
    );
  };
};

/** GET Grid View*/

const GridViewUserResponsibleEntitySchema = z.object({
  id: z.string(),
  name: z.string(),
  tenants: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      personalDataController: z.object({ id: z.string(), name: z.string() }).nullish(),
    })
  ),
});

const GridViewUserSchema = z.object({
  userId: z.string(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  registrationsWhereResponsible: z.array(GridViewUserResponsibleEntitySchema),
  processorRegistrationsWhereResponsible: z.array(GridViewUserResponsibleEntitySchema),
  dpiasWhereResponsible: z.array(GridViewUserResponsibleEntitySchema),
  systemsWhereResponsible: z.array(GridViewUserResponsibleEntitySchema),
});

export type GridViewUser = z.infer<typeof GridViewUserSchema>;

const getUsersGridViewQueryOptions = () =>
  queryOptions({
    queryKey: usersKeys.userGridView(),
    queryFn: () => api.get(`${FORMS_API_BASE_ENDPOINT}/users/grid-view`, { schema: z.array(GridViewUserSchema) }),
    throwOnError: true,
  });
export const prefetchUsersGridView = () => prefetch(getUsersGridViewQueryOptions());
export const useGetUsersGridView = () => useQuery(getUsersGridViewQueryOptions());

export type ReplaceResponsiblePayload = {
  replaceToUserId: string;

  /** The backend only accepts one of the following */
  registrationIds?: string[];
  processorRegistrationIds?: string[];
  dpiaIds?: string[];
  systemIds?: string[];
};

export const ReplaceResponsibleErrorCodes = {
  replaceToUserHasNoPermissionOnAllTenants: "REPLACE_TO_USER_HAS_NO_PERMISSION_ON_ALL_TENANTS",
} as const;

export const useReplaceResponsible = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ReplaceResponsiblePayload) => api.patch(`${FORMS_API_ENDPOINT}/${userId}/replace`, payload),
    onSuccess: () => queryClient.invalidateQueries(),
    onError: error => {
      if (hasErrorCode(error, ReplaceResponsibleErrorCodes.replaceToUserHasNoPermissionOnAllTenants)) {
        void queryClient.invalidateQueries({ queryKey: usersKeys.replaceResponsibleCandidatesAll() });
      }
    },
  });
};

export type ReplaceResponsibleSelection = Omit<ReplaceResponsiblePayload, "replaceToUserId">;

export const replaceResponsibleCandidatesQueryOptions = (userId: string, selection: ReplaceResponsibleSelection) =>
  queryOptions({
    queryKey: usersKeys.replaceResponsibleCandidates(userId, selection),
    queryFn: () =>
      api.post(`${FORMS_API_ENDPOINT}/${userId}/replace/candidates`, selection, {
        schema: z.object({
          users: z.array(
            z.object({
              userId: z.string(),
              email: z.string(),
              firstName: z.string(),
              lastName: z.string(),
              canBeResponsible: z.boolean(),
              tenantsWhereCannotBeResponsible: z.array(z.object({ id: z.string(), name: z.string() })),
            })
          ),
        }),
      }),
    throwOnError: true,
  });
